import {
    Home,
    LayoutDashboard,
    CalendarDays,
    Briefcase,
    Euro,
    Building2,
    GraduationCap,
    Store,
    BookOpen,
    MapPin,
    UserCog,
    Trophy,
    Users,
    Gauge,
    Settings,
    HelpCircle,
    Ticket,
    type LucideIcon,
} from 'lucide-react'

export interface NavItem {
    icon: LucideIcon
    label: string              // i18n key
    href?: string              // Direct URL (if no children)
    permission?: string        // Required permission
    feature?: string           // Required plan feature flag (INSTR-017 §4 — masqué-pas-grisé)
    children?: NavSubItem[]    // Submenu
    /** Pastille de comptage sur une entrée de PREMIER niveau (l'entrée reste
     *  visible à zéro). Rendue dans les deux modes de la barre — étendu (chip
     *  texte) et replié (compteur sur l'icône). */
    counter?: NavCounterKey
}

/** Entrées dont la visibilité dépend d'un compteur, pas d'un plan (INSTR-016b) :
 *  la fonction existe pour tout le monde, mais on n'encombre pas le menu de ceux
 *  qui ne s'en servent jamais. Résolu dans `AppSidebar`. */
export type NavCounterKey =
    | 'instructorRequests'
    | 'pendingApprovals'
    | 'ownCommissions'
    | 'workflowErrors'
    | 'tendersToTriage'
    | 'priceRevisions'

export interface NavSubItem {
    label: string              // i18n key
    href: string
    permission?: string
    /**
     * ⛔ **Une sous-entrée ne porte PAS d'icône — c'est la convention du
     * produit, et elle est visuelle.**
     *
     * Les dix-neuf `icon:` de ce fichier sont TOUS sur des entrées de premier
     * niveau. Au second niveau, le retrait est délibéré : les libellés
     * s'alignent sur une seule colonne de texte, et l'état actif se dit par le
     * fond, pas par un glyphe. Une seule sous-entrée iconisée ne se lit pas
     * comme « celle-ci a une icône » mais comme « celle-ci n'est pas de la
     * même espèce que ses voisines ».
     *
     * Le champ a été ajouté le 2026-09-07 pour « Matériel » (RSC-003 §15
     * l'énonçait), puis RETIRÉ le jour même sur constat du PO à l'écran. La
     * fiche a été recalée : ⚠️ **ne pas le remettre sans changer la convention
     * pour les quatre sous-entrées de Logistique à la fois** — c'est le
     * déséquilibre qui se voit, pas l'icône.
     */
    /**
     * 🔴 **Une entrée qui mène à PLUSIEURS onglets, chacun avec son droit.**
     *
     * `permission` exige UN slug, et c'est le bon défaut. Mais « Suivi &
     * arbitrage » réunit trois onglets gardés séparément : un seul slug y
     * masquait l'entrée à qui avait le droit d'un des deux autres — l'écran
     * existait, la page savait le rendre, et le menu n'y menait pas. Le
     * commentaire de `SalesPilotagePage` promettait d'ailleurs le contraire
     * depuis le début : « l'entrée de menu est masquée sans AUCUN des droits ».
     *
     * ⚠️ Les deux se cumulent en ET quand ils coexistent : `permission` reste un
     * prérequis, `anyPermission` ajoute « et au moins l'un de ceux-là ». En
     * pratique on n'en pose qu'un seul des deux.
     */
    anyPermission?: string[]
    feature?: string           // Required plan feature flag (INSTR-017 §4 — masqué-pas-grisé)
    /** Masque l'entrée tant que le compteur associé vaut 0 (jamais utilisée). */
    hideWhenEmpty?: NavCounterKey
    /** Pastille de comptage — l'entrée reste visible même à zéro. */
    counter?: NavCounterKey
}

export const mainNavItems: NavItem[] = [
    {
        icon: Home,
        label: 'nav.home',
        href: '/',
    },
    {
        icon: LayoutDashboard,
        label: 'nav.dashboard',
        href: '/dashboard',
    },
    {
        icon: Briefcase,
        label: 'nav.commercial',
        permission: 'quotes:read',
        children: [
            // EPIC 11 SALE — routes /sales/*. Doctrine masqué-pas-grisé : chaque
            // sous-entrée hors plan est invisible (feature flag + hasFeature).
            { label: 'nav.quotes', href: '/sales/quotes', permission: 'quotes:read', feature: 'QUOTES' },
            /*
             * ⚠️ **Pas d'entrée « Contrats cadres » ici — décision PO du
             * 2026-09-01.** La table transverse (TND-012) répondait à trois
             * questions de portefeuille : échéances à 90 jours, consommation
             * face au minimum garanti, quel contrat couvre telle formation. Mais
             * la fiche client porte déjà sa section « Contrats cadre » et
             * l'écran transverse n'avait AUCUNE action de ligne : il dupliquait
             * une vue pour un tri global. Intérêt jugé mineur au regard d'une
             * entrée de menu permanente ; on rouvrira si l'usage le réclame.
             *
             * Si elle revient, elle vivra sous CLIENTS et non ici : c'est un
             * objet client, gaté `clients:read`, sans aucun lien avec les
             * marchés publics — c'était d'ailleurs la seule entrée de ce bloc
             * qu'aucun plan ne masquait.
             */
            // SALE-025 — le plan passe par `feature`, jamais par un test de tier :
            // `canAccess` s'appuie sur `hasFeature()`, qui échoue FERMÉ pendant le
            // chargement de la configuration. `isBusiness`, lui, échoue OUVERT
            // (`plan: 'business'` par défaut) et ferait clignoter l'entrée chez un FOCUS.
            // SALE-026 — `commissions:view_own` est accordée à tout bénéficiaire :
            // vérifier le calcul de sa propre rémunération variable est une
            // obligation, pas une option. Le plan passe par `feature`, jamais par
            // un test de tier écrit à la main.
            // ⚠️ `hideWhenEmpty` — l'entrée n'apparaît QUE si un barème concerne
            // réellement la personne (arbitrage PO 2026-08-13) : un menu qui
            // propose un bulletin éternellement vide est du bruit. Le drapeau
            // vient du serveur (`/commissions/self-status`), jamais d'un test
            // de plan écrit à la main.
            { label: 'nav.commissions', href: '/sales/commissions', permission: 'commissions:view_own', feature: 'SALES_COMMISSIONS', hideWhenEmpty: 'ownCommissions' },
            // SALE-025 + SALE-026 (arbitrage PO 2026-08-13) — UNE entrée pour les
            // deux suivis du responsable, deux onglets à l'intérieur : les deux
            // s'adressent à la même personne dans le même geste, arbitrer. Le
            // compteur porte les DÉROGATIONS EN ATTENTE et elles seules — une
            // commission ne réclame rien, une dérogation bloque un devis.
            // 🔴 QUATRE droits, parce que l'écran a TROIS onglets gardés
            // séparément (§13.35) et que celui des commissions en accepte DEUX.
            // Avec le seul `discount:approve`, un responsable qui suit les
            // commissions ou les objectifs de son équipe ne voyait pas l'entrée
            // — alors que la page lui aurait affiché son onglet.
            //
            // ⚠️ `commissions:view_team` manquait, et c'était une entrée MORTE :
            // le droit a été créé le 2026-08-13 pour le chef des ventes qui ne
            // doit voir que son équipe, et l'écran exigeait `view_all` — celui
            // que `view_team` existait précisément pour éviter. Invisible sur les
            // tenants existants (ils portent les deux, migration 460), mais le
            // seed des NOUVEAUX n'accorde que `view_team` : le prochain
            // organisme créé aurait eu un gestionnaire sans son écran. Le compteur, lui, ne porte que les
            // DÉROGATIONS EN ATTENTE : une commission ne réclame rien, un
            // objectif encore moins, une dérogation bloque un devis.
            { label: 'nav.salesPilotage', href: '/sales/pilotage', anyPermission: ['discount:approve', 'commissions:view_all', 'commissions:view_team', 'sales_profile:read'], feature: 'DISCOUNT_APPROVALS', counter: 'pendingApprovals' },
            { label: 'nav.pendingRequests', href: '/sales/pending-requests', permission: 'quotes:read', feature: 'SALES_PENDING_REQUESTS' },
            { label: 'nav.waitlists', href: '/sales/waitlists', permission: 'quotes:read', feature: 'SALES_WAITLISTS' },
            { label: 'nav.planningRequests', href: '/sales/planning-requests', permission: 'quotes:read', feature: 'SALES_PLANNING_REQUESTS' },
            { label: 'nav.renewals', href: '/sales/renewals', permission: 'quotes:read', feature: 'SALES_RENEWALS' },
            /*
             * EPIC 40 TND — Appels d'offres & marchés publics. **En BAS de la
             * section, décision PO du 2026-09-01.**
             *
             * L'entrée est gatée `TENDERS` (plan BUSINESS) : pour la majorité
             * des organismes elle n'existe pas. La placer en troisième position
             * creusait un trou dans la liste de tous les autres, alors que le
             * flux quotidien — devis, commandes, renouvellements — vit au-dessus.
             *
             * ⚠️ La `permission` du PARENT (`quotes:read`) n'est PAS un
             * prérequis ici : `renderNavItem` ne la teste que pour une entrée
             * SANS enfants — une section n'est masquée que si aucun de ses
             * enfants n'est visible. Un utilisateur qui ne porte que
             * `tenders:read` verra donc bien la section Commercial et cette
             * seule entrée. (Vérifié dans `AppSidebar.renderNavItem`.)
             *
             * ⚠️ **UNE SEULE entrée pour tout le module — décision PO du
             * 2026-09-01.** Trois destinations autonomes ont été repliées dans
             * le menu d'outils de la liste :
             *   • la bibliothèque de réponse (deux catalogues qu'on venait
             *     remplir et jamais utiliser) ;
             *   • les réglages du pipeline ;
             *   • **les critères de veille** — la zone « Veille de marché » a
             *     disparu, ses avis sont désormais des LIGNES du tableau au
             *     stage `VEILLE`. Un second inbox est un second endroit qu'on
             *     oublie de regarder ; et surtout, un avis écarté dans le feed
             *     écrivait un `dismissed_reason` en texte libre qui n'atteignait
             *     jamais `tender_outcome_reasons` — la tuile « Motifs de
             *     non-réponse » ne pouvait donc pas voir la principale source de
             *     non-réponses.
             *
             * ⚠️ **Le compteur porte désormais le stage `VEILLE`, pas un feed.**
             * Il compte des FICHES à trier, dédupliquées par construction (une
             * fiche, une ligne) — là où l'ancien comptait des avis dont
             * `matched_profile_ids` pouvait faire doubler l'un par deux
             * critères. Et il ne retombe plus quand on REGARDE : seulement quand
             * on DÉCIDE — qualifier ou abandonner avec un motif. Une pastille
             * qui s'éteint au coup d'œil ment sur la charge restante.
             */
            { label: 'nav.tenders', href: '/sales/tenders', permission: 'tenders:read', feature: 'TENDERS', counter: 'tendersToTriage' },
        ],
    },
    {
        icon: CalendarDays,
        label: 'nav.sessions',
        href: '/sessions',
        permission: 'sessions:read',
    },
    {
        icon: Euro,
        label: 'nav.billing',
        href: '/billing',
        permission: 'invoices:read',
    },
    {
        icon: Building2,
        label: 'nav.clients',
        permission: 'clients:read',
        children: [
            { label: 'nav.clientsList', href: '/clients', permission: 'clients:read' },
            /*
             * TND-013 — la vue transverse « Révisions de prix ». Sous CLIENTS, et
             * non sous Commercial : un contrat cadre est un objet client (TND-012
             * §0.3, arbitrage PO du 2026-09-01).
             *
             * Gatée comme les contrats cadre eux-mêmes — masqués en FOCUS par
             * `isFocus` (`ClientFinanceTab`) : il n'existe AUCUNE feature de plan
             * pour eux, donc pas de `feature` ici. Le plan est lu par la barre
             * latérale, qui n'interroge pas le serveur en FOCUS ni pendant le
             * chargement de la configuration (échec FERMÉ). `hideWhenEmpty` masque
             * l'entrée tant qu'aucun contrat ne porte de clause de révision.
             */
            {
                label: 'nav.priceRevisions',
                href: '/clients/price-revisions',
                permission: 'clients:read',
                hideWhenEmpty: 'priceRevisions',
            },
        ],
    },
    // L'item `nav.contacts` (page transverse `/crm/contacts`) a été retiré
    // le 2026-05-02. Sa fonctionnalité sera reprise par DIR-001 (Annuaire
    // global) qui exposera une vue multi-sources sous `/directory`.
    {
        icon: GraduationCap,
        label: 'nav.learners',
        href: '/learners',
        permission: 'learners:read',
    },
    {
        // INSTR-017 §4 — section à 3 enfants (les cockpits sont masqués, jamais
        // grisés, hors plan BUSINESS via la feature INSTRUCTOR_COCKPIT).
        icon: UserCog,
        label: 'nav.instructors',
        permission: 'instructors:read',
        children: [
            { label: 'nav.instructorsList', href: '/instructors', permission: 'instructors:read' },
            {
                // INSTR-016b — console des demandes envoyées. AUCUN `feature` :
                // INSTR-016 est « tous plans », et son seul écran de sortie était
                // jusqu'ici gaté BUSINESS. `hideWhenEmpty` la masque tant qu'aucune
                // demande n'a jamais été émise : un organisme qui n'utilise pas la
                // fonction ne voit pas l'entrée, mais elle ne disparaît plus une
                // fois apparue (l'historique reste accessible).
                label: 'nav.instructorsRequests',
                href: '/instructors/requests',
                permission: 'instructors:read',
                hideWhenEmpty: 'instructorRequests',
            },
            {
                label: 'nav.instructorsRequirements',
                href: '/instructors/requirements',
                permission: 'instructors:read',
                feature: 'INSTRUCTOR_COCKPIT',
            },
            {
                label: 'nav.instructorsCoverage',
                href: '/instructors/coverage',
                permission: 'instructors:read',
                feature: 'INSTRUCTOR_COCKPIT',
            },
        ],
    },
    {
        icon: Store,
        label: 'nav.catalog',
        permission: 'catalog:read',
        children: [
            { label: 'nav.catalogFormations', href: '/catalog/formations', permission: 'catalog:read' },
            { label: 'nav.catalogPrestations', href: '/catalog/prestations', permission: 'catalog:read' },
            { label: 'nav.catalogArticles', href: '/catalog/articles', permission: 'catalog:read' },
            { label: 'nav.catalogFrais', href: '/catalog/frais', permission: 'catalog:read' },
        ],
    },
    {
        icon: BookOpen,
        label: 'nav.pedagogy',
        children: [
            { label: 'nav.pedagogyAssessments', href: '/pedagogy/assessments', permission: 'assessment:read' },
            { label: 'nav.pedagogyElearning', href: '/pedagogy/elearning', permission: 'elearning:read' },
            { label: 'nav.pedagogyReferentials', href: '/pedagogy/referentials', permission: 'pedagogy:read' },
        ],
    },
    {
        icon: MapPin,
        label: 'nav.logistics',
        permission: 'locations:read',
        children: [
            { label: 'nav.logisticsLocations', href: '/logistics/locations', permission: 'locations:read' },
            { label: 'nav.logisticsCatering', href: '/logistics/catering', permission: 'catering:read' },
            { label: 'nav.logisticsAccommodation', href: '/logistics/accommodation', permission: 'accommodation:read' },
            { label: 'nav.logisticsResources', href: '/logistics/resources', permission: 'resources:read' },
        ],
    },
    {
        icon: Trophy,
        label: 'nav.quality',
        permission: 'quality:read',
        children: [
            { label: 'nav.nonConformities', href: '/quality/non-conformities', permission: 'quality:read', feature: 'QUALITY' },
            { label: 'nav.actionPlan', href: '/quality/action-plan', permission: 'quality:read', feature: 'QUALITY' },
            { label: 'nav.qualiopiDashboard', href: '/quality/qualiopi', permission: 'quality:read', feature: 'QUALITY' },
            { label: 'nav.satisfaction', href: '/quality/satisfaction', permission: 'satisfaction:read', feature: 'SATISFACTION' },
            { label: 'nav.monitoring', href: '/quality/monitoring', permission: 'quality:read', feature: 'QUALITY' },
            // Écran d'administration des 5 référentiels NC (origine, vecteur, cause,
            // catégorie et structure du demandeur). Il était JOIGNABLE UNIQUEMENT en
            // tapant l'URL, alors que la modale de suppression y renvoie explicitement
            // (« … seulement désactivée »).
        ],
    },
    // DIR-001 — Annuaire transverse de toutes les personnes physiques
    // (contacts clients, apprenants, intervenants, contacts logistiques).
    // Item top-level juste en dessous de Qualité.
    {
        icon: Users,
        label: 'nav.directory',
        href: '/directory',
        permission: 'directory:read',
    },
    // « Suivi & automatisation » — deux façons de répondre à UNE question :
    // *ce qui doit être fait, l'est-il ?* Les checklists y répondent parce qu'un
    // humain coche ; les workflows, parce que ça tourne tout seul. Le libellé
    // nomme les DEUX natures côte à côte, et ne range pas l'une sous l'autre :
    // une checklist n'automatise rien (arbitrage PO 2026-08-30, qui a sorti les
    // checklists de Réglages → Automatisation où elles étaient mal classées).
    //
    // « Suivi » vient en premier, et l'ordre des enfants aussi : les checklists
    // sont un outil QUOTIDIEN, les exécutions de workflow une console qu'on
    // consulte quand la pastille s'allume.
    {
        icon: Gauge,
        label: 'nav.tracking',
        children: [
            // CHK-004 — la banque des modèles de checklist. Elle vit ICI et non
            // dans les Réglages : ses modèles sont le MATÉRIAU de travail du
            // module, pas sa configuration. Un workflow, une fois défini, tourne
            // seul — sa définition est un réglage. Une checklist ne tourne jamais
            // seule. (CHK-014 ajoutera « Mon suivi », /checklists/my-tracking.)
            {
                label: 'nav.checklists',
                href: '/checklists',
                permission: 'checklists:read',
                feature: 'CHECKLISTS',
            },
            // WKF (moteur, EPIC 12) — console d'EXÉCUTION (runs passé/futur, tous
            // plans). Doctrine masqué-pas-grisé : visible dès le flag WORKFLOWS +
            // permission workflows:view_runs. La GESTION des définitions (créer /
            // activer-pause / éditer) vit dans Réglages → Automatisation
            // (/settings/workflows), pas ici — et cette asymétrie avec les
            // checklists est VOULUE, cf. ci-dessus.
            {
                label: 'nav.workflows',
                href: '/automation/workflows',
                permission: 'workflows:view_runs',
                feature: 'WORKFLOWS',
                // Pastille des automatisations en ÉCHEC (WKF-008 §6.1). Une
                // automatisation qui échoue est silencieuse par nature : personne ne
                // va consulter un historique pour vérifier qu'un e-mail est bien
                // parti. Sans ce signal, il fallait qu'un client se plaigne de ne
                // rien avoir reçu pour qu'on découvre la panne.
                // ⚠️ Elle survit à la descente d'un cran : `AppSidebar` rend
                // `sub.counter` sur les enfants comme `item.counter` sur les
                // feuilles (vérifié 2026-08-30).
                counter: 'workflowErrors',
            },
        ],
    },
    // WKF-013 — Centre de notifications & tâches : PAS d'entrée nav dédiée
    // (arbitrage PO 2026-07-06). Le centre personnel s'ouvre via la CLOCHE 🔔
    // du header (toujours visible + pied « Ouvrir le centre de notifications »)
    // → redondant avec une entrée sidebar. La gouvernance org (réaffectation de
    // masse des tâches) vit dans Réglages → zone « automation » (carte
    // « Administration des tâches », gate tasks:manage_all), et sera regroupée
    // avec les défauts de notifications de l'organisme dans /settings/notifications
    // au lot NOTIF-001. Préférences perso = /account/notifications (PREF-005).
]

export const settingsNavItems: NavItem[] = [
    {
        icon: Settings,
        label: 'nav.settings',
        href: '/settings',
        permission: 'settings:read',
    },
    {
        icon: HelpCircle,
        label: 'nav.help',
        href: '/knowledge',
    },
    {
        icon: Ticket,
        label: 'nav.support',
        href: '/support/tickets',
    },
]
